import { useEffect, useState, useCallback, useRef } from "react";
import { apiGet, apiPost, downloadCheckInsCsv } from "../lib/api";
import { Search, Download, RefreshCw, LogOut } from "lucide-react";
import type { RenderedCustomField } from "@vms/shared";

type StatusFilter = "all" | "on_site" | "checked_out";

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "on_site", label: "On site now" },
  { value: "checked_out", label: "Checked out" },
];

interface OnSiteSummary {
  total: number;
  locations: { locationId: string; locationName: string; count: number }[];
}

interface CheckInRecord {
  id: string;
  visitorName: string;
  maskedNric: string;
  nationality: string | null;
  email: string | null;
  phone: string | null;
  purpose: string | null;
  checkInAt: string;
  checkOutAt: string | null;
  location: { id: string; name: string };
  dob: string | null;
  customFields?: RenderedCustomField[];
}

interface LocationOption {
  id: string;
  name: string;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export function CheckIns() {
  const [checkIns, setCheckIns] = useState<CheckInRecord[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<CheckInRecord | null>(null);
  const [checkingOutId, setCheckingOutId] = useState<string | null>(null);

  const [locationId, setLocationId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [onSite, setOnSite] = useState<OnSiteSummary | null>(null);
  const [page, setPage] = useState(1);

  const pollRef = useRef<ReturnType<typeof setInterval>>(undefined);

  // The search term may be a full NRIC/FIN, so every filter travels in the POST
  // body — a URL would leak it into access logs and browser history.
  const buildFilters = useCallback(
    () => ({
      status,
      ...(locationId && { locationId }),
      ...(from && { from }),
      ...(to && { to }),
      ...(debouncedSearch.trim() && { q: debouncedSearch.trim() }),
    }),
    [status, locationId, from, to, debouncedSearch],
  );

  const fetchCheckIns = useCallback(async () => {
    const res = await apiPost<CheckInRecord[]>("/checkins/search", { ...buildFilters(), page, pageSize: 50 });
    if (res.success) {
      setCheckIns(res.data || []);
      if (res.pagination) setPagination(res.pagination);
    }
    setLoading(false);
  }, [buildFilters, page]);

  const fetchOnSite = useCallback(async () => {
    const res = await apiGet<OnSiteSummary>("/checkins/on-site");
    if (res.success && res.data) setOnSite(res.data);
  }, []);

  useEffect(() => {
    apiGet<LocationOption[]>("/locations").then((res) => {
      if (res.success && res.data) setLocations(res.data);
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    fetchCheckIns();
  }, [fetchCheckIns]);

  useEffect(() => {
    fetchOnSite();
  }, [fetchOnSite]);

  // Poll every 10 seconds for real-time updates
  useEffect(() => {
    pollRef.current = setInterval(() => { fetchCheckIns(); fetchOnSite(); }, 10000);
    return () => clearInterval(pollRef.current);
  }, [fetchCheckIns, fetchOnSite]);

  function handleExport() {
    void downloadCheckInsCsv(buildFilters()).catch((err) => console.error("[export]", err));
  }

  async function handleCheckout(checkIn: CheckInRecord) {
    if (!window.confirm(`Check out ${checkIn.visitorName}? This cannot be undone.`)) return;
    setCheckingOutId(checkIn.id);
    try {
      const res = await apiPost<CheckInRecord>(`/checkins/${checkIn.id}/checkout`, {});
      if (!res.success || !res.data) {
        window.alert(res.error?.message ?? "Unable to check out visitor");
        return;
      }
      setSelected((current) => current?.id === checkIn.id ? res.data! : current);
      await Promise.all([fetchCheckIns(), fetchOnSite()]);
    } finally {
      setCheckingOutId(null);
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 font-display">Check-Ins</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchCheckIns()}
            className="p-2 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 bg-white border border-stone-300 rounded-lg hover:bg-stone-50"
          >
            <Download size={16} />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
        </div>
      </div>

      {/* On site now */}
      <div className="mb-4 bg-white rounded-xl border border-stone-200 p-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-sm font-medium text-stone-500">On site now</span>
          <span className="text-2xl font-semibold text-stone-900 font-display">{onSite?.total ?? "—"}</span>
        </div>
        {onSite && onSite.locations.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {onSite.locations.map((loc) => (
              <button
                key={loc.locationId}
                onClick={() => { setStatus("on_site"); setLocationId(loc.locationId); setPage(1); }}
                className={`px-3 py-1.5 text-sm rounded-full border ${
                  status === "on_site" && locationId === loc.locationId
                    ? "bg-brand-600 border-brand-600 text-white"
                    : "bg-stone-50 border-stone-200 text-stone-700 hover:bg-stone-100"
                }`}
              >
                {loc.locationName}
                <span className="ml-2 font-semibold tabular-nums">{loc.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Status */}
      <div className="inline-flex p-1 mb-4 bg-stone-100 rounded-lg">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => { setStatus(tab.value); setPage(1); }}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              status === tab.value ? "bg-white text-stone-900 shadow-sm" : "text-stone-600 hover:text-stone-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap gap-3 mb-4">
        <div className="relative sm:col-span-2 lg:flex-1 lg:min-w-[200px] lg:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder="Search name, NRIC, email or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
        <select
          value={locationId}
          onChange={(e) => { setLocationId(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">All locations</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>{loc.name}</option>
          ))}
        </select>
        <input
          type="date"
          value={from}
          onChange={(e) => { setFrom(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          placeholder="From"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => { setTo(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          placeholder="To"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200">
                <th className="text-left px-4 py-3 font-medium text-stone-600">Visitor</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600">NRIC</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600">Email</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600">Phone</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600">Location</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600">Check In</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600">Check Out</th>
                <th className="text-right px-4 py-3 font-medium text-stone-600">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-stone-500">Loading...</td>
                </tr>
              ) : checkIns.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-stone-400">No check-ins found</td>
                </tr>
              ) : (
                checkIns.map((ci) => (
                  <tr key={ci.id} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => setSelected(ci)}>
                    <td className="px-4 py-3 font-medium text-stone-900">{ci.visitorName}</td>
                    <td className="px-4 py-3 text-stone-600 font-mono text-xs">{ci.maskedNric}</td>
                    <td className="px-4 py-3 text-stone-600">{ci.email || "—"}</td>
                    <td className="px-4 py-3 text-stone-600">{ci.phone || "—"}</td>
                    <td className="px-4 py-3 text-stone-600">{ci.location.name}</td>
                    <td className="px-4 py-3 text-stone-600">{formatDate(ci.checkInAt)}</td>
                    <td className="px-4 py-3 text-stone-600">
                      {ci.checkOutAt ? formatDate(ci.checkOutAt) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded-full">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!ci.checkOutAt && <button
                        onClick={(event) => { event.stopPropagation(); void handleCheckout(ci); }}
                        disabled={checkingOutId === ci.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-stone-700 border border-stone-300 rounded-lg hover:bg-stone-100 disabled:opacity-50"
                      >
                        <LogOut size={14} />
                        {checkingOutId === ci.id ? "Checking out..." : "Check out"}
                      </button>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-stone-200">
            <p className="text-sm text-stone-500">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm border border-stone-300 rounded-lg hover:bg-stone-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm border border-stone-300 rounded-lg hover:bg-stone-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      {selected && <CheckInDetail checkIn={selected} onClose={() => setSelected(null)} onCheckout={handleCheckout} checkingOut={checkingOutId === selected.id} />}
    </div>
  );
}

function CheckInDetail({ checkIn, onClose, onCheckout, checkingOut }: { checkIn: CheckInRecord; onClose: () => void; onCheckout: (checkIn: CheckInRecord) => Promise<void>; checkingOut: boolean }) {
  const custom = checkIn.customFields ?? [];
  return <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}><div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
    <div className="flex justify-between"><h2 className="text-lg font-semibold">Check-in details</h2><button onClick={onClose}>×</button></div>
    <h3 className="text-xs uppercase text-stone-500 font-medium mt-5">Singpass details</h3>
    <dl className="mt-2 divide-y">{[["Name", checkIn.visitorName], ["NRIC/FIN", checkIn.maskedNric], ["Date of birth", checkIn.dob], ["Email", checkIn.email], ["Phone", checkIn.phone]].map(([label, value]) => <div key={label} className="flex justify-between py-2 gap-4"><dt className="text-sm text-stone-500">{label}</dt><dd className="text-sm text-right">{value || "—"}</dd></div>)}</dl>
    {custom.length > 0 && <><h3 className="text-xs uppercase text-stone-500 font-medium mt-5">Manual details</h3><dl className="mt-2 divide-y">{custom.map((field) => <div key={field.id} className="flex justify-between py-2 gap-4"><dt className="text-sm text-stone-500">{field.label}</dt><dd className="text-sm text-right">{field.displayValue || "—"}</dd></div>)}</dl></>}
    {!checkIn.checkOutAt && <button onClick={() => void onCheckout(checkIn)} disabled={checkingOut} className="mt-6 w-full inline-flex justify-center items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50"><LogOut size={16} />{checkingOut ? "Checking out..." : "Check out visitor"}</button>}
  </div></div>;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-SG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
